import { type AstArg, type AstNode, NO_POS, type SourcePos } from './ast.ts';
import { ShapeError, SourceOpInPipelineError } from './errors.ts';
import type { Opcode } from './op-surface.ts';
import { elemPath, mapValuePath, nameKeyPath, paramPath, parentPath } from './path.ts';
import { SOURCE_OP_NAMES } from './providers/groups.ts';
import type { Dataset, Value } from './value.ts';

/**
 * Normalized JS-object AST. The evaluator dispatches over this shape, not
 * the parser's AstNode tree. Two consequences:
 *
 * - The engine accepts pre-built JS objects too — an LLM can emit JSON
 *   directly and skip the parser entirely.
 * - Each op's Zod schema validates this shape.
 *
 * Shapes:
 * - Embedded values — any self-evaluating value that is NOT a structural
 *   marker ($sym/$kw/$map/op-call): primitives (number / string / boolean /
 *   null / Date), `Dataset` instances inserted by host code, AND opaque
 *   provider objects (e.g. a raw `{ space, externalId }` instance
 *   reference). The s-expr parser cannot produce `Dataset`/opaque values;
 *   they enter via direct JS-object input or as op return values.
 * - Arrays — vectors
 * - `{ $sym: 'name' }` — symbol references (looked up in env on evaluation)
 * - `{ name: 'op', params: [...], <kwarg>: ... }` — op calls
 */
export type AstInput =
  | number
  | string
  | boolean
  | null
  | Date
  | Dataset
  | { readonly [key: string]: unknown }
  | readonly AstInput[]
  | { readonly $sym: string }
  | { readonly $kw: string }
  | { readonly $map: readonly { readonly k: string; readonly v: AstInput }[] }
  | OpCall;

/**
 * An op call after normalization. The discriminator is `name`. Positional
 * args live in `params`; kwargs are top-level fields. Op schemas declare the
 * exact shapes they accept; the indexer is `unknown` here so type-narrowing
 * on sibling shapes (e.g. `{ $sym }`) isn't poisoned by an open
 * `AstInput`-typed indexer.
 */
export type OpCall = {
  readonly name: Opcode;
  readonly params?: readonly AstInput[];
  readonly [kwarg: string]: unknown;
};
/**
 * Type guard: is this AST node an op call? An op call is a non-array
 * object with a string `name` field. Used by the evaluator's dispatch and
 * by `transform`'s verb-AST validation.
 */
export const isOpCall = (node: unknown): node is OpCall =>
  node !== null &&
  typeof node === 'object' &&
  !Array.isArray(node) &&
  'name' in node &&
  typeof (node as { name: unknown }).name === 'string';

/**
 * Tagged discriminant view over `AstInput`. The structural union has no
 * tag field, so every walker (`evaluator.evalNode`, `runtime/arquero.compileExpr`)
 * was repeating the same `null` / `typeof` / `instanceof` / `$sym` / `name`
 * chain to discriminate. `astShape` centralises that dispatch and gives
 * callers a tagged shape they can `switch` over with full exhaustiveness:
 * adding a new `AstInput` variant requires updating only `astShape`'s body
 * (and the `AstShape` union), and every consumer fails to compile until it
 * handles the new tag.
 */
export type AstShape =
  | { readonly kind: 'embedded'; readonly value: Value }
  | { readonly kind: 'vector'; readonly elements: readonly AstInput[] }
  | { readonly kind: 'symbolRef'; readonly sym: string }
  | { readonly kind: 'keyword'; readonly name: string }
  | { readonly kind: 'map'; readonly entries: readonly { readonly k: string; readonly v: AstInput }[] }
  | { readonly kind: 'opCall'; readonly call: OpCall };

/**
 * Canonical message for the `ShapeError` raised when an `embedded` value is
 * an opaque object that no provider identity contract claims — a malformed
 * value in expression position. Malformed-value detection lives in the
 * type-check pass (`schema/infer.inferExprType` consults the threaded
 * `IdentityTable`); `astShape` itself never raises, as it is pure-structural.
 */
export const INVALID_AST_SHAPE_MESSAGE = 'expected an expression: literal, column reference, or op call';

/**
 * Discriminate an `AstInput`-shaped (or actually anything — accepts `unknown`
 * for the consumer's narrowing convenience) value to its tagged shape.
 *
 * PURE-STRUCTURAL: the only questions asked are "is this a structural marker
 * ($sym/$kw/$map/op-call) or a vector?". Everything else — null/number/
 * string/boolean/Date/Dataset AND opaque provider objects (a raw
 * `{ space, externalId }` instance reference) — is a self-evaluating
 * `embedded` value. `astShape` NEVER consults an identity registry and
 * NEVER classifies an opaque object as invalid: whether an embedded value
 * is a row identity (and whether an opaque object is malformed) is a
 * TYPE-layer question, answered later by `inferExprType` against the
 * threaded `IdentityTable`.
 */
export const astShape = (node: unknown): AstShape => {
  if (Array.isArray(node)) {
    return { kind: 'vector', elements: node };
  }
  if (
    typeof node === 'object' &&
    node !== null &&
    '$sym' in node &&
    typeof (node as { $sym: unknown }).$sym === 'string' &&
    !('name' in node)
  ) {
    return { kind: 'symbolRef', sym: (node as { $sym: string }).$sym };
  }
  if (
    typeof node === 'object' &&
    node !== null &&
    '$kw' in node &&
    typeof (node as { $kw: unknown }).$kw === 'string' &&
    !('name' in node)
  ) {
    return { kind: 'keyword', name: (node as { $kw: string }).$kw };
  }
  if (
    typeof node === 'object' &&
    node !== null &&
    '$map' in node &&
    Array.isArray((node as { $map: unknown }).$map) &&
    !('name' in node)
  ) {
    return { kind: 'map', entries: (node as { $map: readonly { readonly k: string; readonly v: AstInput }[] }).$map };
  }
  if (isOpCall(node)) {
    return { kind: 'opCall', call: node };
  }
  return { kind: 'embedded', value: node as Value };
};
/**
 * Handler table for {@link foldAst}: one entry per `AstShape` variant,
 * keyed by the variant's `kind`. Each handler receives the already
 * narrowed `AstShape` member and returns `ResultType`.
 *
 * The key set is derived from `AstShape` itself, so it is the SSOT for
 * "which variants exist": adding a variant to the `AstShape` union makes
 * this type require a new handler key, which is a compile error at every
 * `foldAst` call site (`evaluator.evalNode`, `runtime/arquero.compileExpr`)
 * until that site handles the new variant.
 *
 * Each handler keeps full control of its own recursion — `foldAst` only
 * owns the exhaustive dispatch, not the descent strategy. Walkers that
 * thread context, recurse asynchronously, or descend conditionally close
 * over their own state in the handler bodies.
 */
export type AstShapeHandlers<ResultType> = {
  readonly [KindType in AstShape['kind']]: (shape: Extract<AstShape, { kind: KindType }>) => ResultType;
};

/**
 * Single exhaustive dispatch over the `AstShape` union. Discriminates
 * `node` via {@link astShape}, then routes to the matching handler. The
 * `never`-guarded `default` arm makes the switch the SSOT for the variant
 * enumeration: any `AstShape` variant left unhandled fails to compile here
 * AND forces every `foldAst` caller to supply the new handler (see
 * {@link AstShapeHandlers}).
 *
 * `foldAst` does NOT recurse on its own — handlers own descent. This keeps
 * the two production walkers (eager-async evaluation vs per-row
 * compilation) sharing one dispatch without contorting either to a rigid
 * post-order fold.
 */
export const foldAst = <ResultType>(node: unknown, handlers: AstShapeHandlers<ResultType>): ResultType => {
  const shape = astShape(node);
  switch (shape.kind) {
    case 'embedded':
      return handlers.embedded(shape);
    case 'vector':
      return handlers.vector(shape);
    case 'symbolRef':
      return handlers.symbolRef(shape);
    case 'keyword':
      return handlers.keyword(shape);
    case 'map':
      return handlers.map(shape);
    case 'opCall':
      return handlers.opCall(shape);
    default: {
      const _exhaustive: never = shape;
      return _exhaustive;
    }
  }
};

/**
 * Convenience: "is this AST node a bare column symbol?" — returns
 * the symbol name when yes, `undefined` otherwise. Thin wrapper over
 * {@link astShape}'s symbolRef arm. SSOT for the common "fast-path
 * iff bare-symbol" check used by agg-funcs (schema propagation) and
 * the lift helper (sort / window / pivot column-or-expression args).
 */
export const asColSymbol = (node: unknown): string | undefined => {
  const shape = astShape(node);
  return shape.kind === 'symbolRef' ? shape.sym : undefined;
};
/**
 * Convenience: the string value of a self-evaluating `embedded` AST node, or
 * `undefined`. Thin wrapper over {@link astShape}'s embedded arm — the SSOT for
 * the "read a string literal off an AST node" check shared by column-ref
 * decoding, op-label parsing, and pattern / name literals.
 */
export const embeddedString = (node: unknown): string | undefined => {
  const shape = astShape(node);
  return shape.kind === 'embedded' && typeof shape.value === 'string' ? shape.value : undefined;
};

/**
 * Convenience: the number value of a self-evaluating `embedded` AST node, or
 * `undefined`. Companion to {@link embeddedString}.
 */
export const embeddedNumber = (node: unknown): number | undefined => {
  const shape = astShape(node);
  return shape.kind === 'embedded' && typeof shape.value === 'number' ? shape.value : undefined;
};

/**
 * Convenience: a homogeneous string-literal set from an AST node — a single
 * `embedded` string yields a one-element list; a `vector` yields its elements
 * only when EVERY element is a string literal (all-or-nothing). `undefined` for
 * anything else. SSOT for "the literal string set this node names".
 */
export const embeddedStringVector = (node: unknown): readonly string[] | undefined => {
  const shape = astShape(node);
  if (shape.kind === 'embedded') {
    return typeof shape.value === 'string' ? [shape.value] : undefined;
  }
  if (shape.kind !== 'vector') {
    return undefined;
  }
  const out = shape.elements.flatMap((el) => {
    const s = embeddedString(el);
    return s === undefined ? [] : [s];
  });
  return out.length === shape.elements.length ? out : undefined;
};

/**
 * Is this AST node a top-level `(and …)` call? SSOT for the
 * "boolean-AND conjunction" predicate recognised by both the provider
 * partition split (`splitPredicate`) and the ancestor-scope pushdown
 * (`andChildren`) — neither re-derives the `name === 'and'` check.
 */
export const isAndCall = (node: unknown): boolean => {
  const shape = astShape(node);
  return shape.kind === 'opCall' && shape.call.name === 'and';
};

/**
 * Flatten a predicate into its top-level AND conjuncts: the children of
 * a top `(and …)`, or the node itself wrapped as a single-element list.
 * SSOT for "split a conjunction into its leaves", shared by the provider
 * partition walk and the ancestor-scope pushdown — replacing two
 * byte-divergent re-implementations of the same flatten.
 */
export const andChildren = (node: AstInput): readonly AstInput[] => {
  const shape = astShape(node);
  if (shape.kind === 'opCall' && shape.call.name === 'and') {
    return (shape.call.params ?? []) as readonly AstInput[];
  }
  return [node];
};

/**
 * Extract the body of a lambda AST node — `(fn [%] <body>)` →
 * `[<body>, 'params/1']`. Returns `undefined` when `node` isn't a
 * normalized `fn` opcall. The relative path `'params/1'` points from
 * the lambda's own root to the body, suitable for composing with the
 * lambda's own path to produce a Spans-key.
 *
 * SSOT for "decode a lambda's predicate body without throwing", shared
 * by `(filter)`'s referencedColumns / resolveSetRefs hooks and by the
 * CDF view provider's filter push-down — both consume the same lambda
 * syntax (`#(…)` reader macro desugared to `(fn [%] …)`).
 */
export const extractLambdaBody = (node: unknown): readonly [body: AstInput, relPath: string] | undefined => {
  const shape = astShape(node);
  if (shape.kind !== 'opCall' || shape.call.name !== 'fn') {
    return undefined;
  }
  const body = shape.call.params?.[1];
  return body === undefined ? undefined : [body as AstInput, 'params/1'];
};
/**
 * Extract a lambda AST node's parameter names — `(fn [%] …)` → `['%']`,
 * `(fn [%1 %2] …)` → `['%1','%2']`. Returns `undefined` when `node`
 * isn't a normalized `fn` opcall or any param isn't a plain symbol.
 *
 * SSOT for "this lambda binds these names to its arguments", consumed
 * by the predicate set-ref rewrite to build the row-alias set: inside
 * the body, a `(col …)` whose head is one of these params is a row
 * reference, never a foreign set-ref.
 */
export const extractLambdaParams = (node: unknown): readonly string[] | undefined => {
  const shape = astShape(node);
  if (shape.kind !== 'opCall' || shape.call.name !== 'fn') {
    return undefined;
  }
  const paramVec = astShape(shape.call.params?.[0] as AstInput);
  if (paramVec.kind !== 'vector') {
    return undefined;
  }
  const names = paramVec.elements.map((el) => {
    const s = astShape(el);
    return s.kind === 'symbolRef' ? s.sym : undefined;
  });
  return names.every((n): n is string => n !== undefined) ? (names as readonly string[]) : undefined;
};
/**
 * Collect every lambda parameter name bound ANYWHERE inside `node` — the
 * union of each nested `(fn […] …)`'s params. Walks positional params,
 * vector elements, and an `OpCall`'s lifted kwarg fields, so a predicate
 * body reached via any arg position is covered.
 *
 * The static type-check uses this to build the row-alias set for
 * `colRefKind`, mirroring what the runtime sites obtain from
 * `extractLambdaParams` on their own lambda — so "is this `(col …)` head
 * a row reference?" is decided identically before and during evaluation.
 */
export const collectFnParamNames = (node: unknown): ReadonlySet<string> => {
  const names = new Set<string>();
  const visit = (n: unknown): void => {
    const shape = astShape(n);
    if (shape.kind === 'vector') {
      shape.elements.forEach(visit);
      return;
    }
    if (shape.kind !== 'opCall') {
      return;
    }
    if (shape.call.name === 'fn') {
      (extractLambdaParams(n) ?? []).forEach((p) => {
        names.add(p);
      });
    }
    (shape.call.params ?? []).forEach(visit);
    // Lifted kwarg fields (anything beyond `name`/`params`) may carry a
    // lambda. Recurse their values too.
    Object.entries(shape.call as Record<string, unknown>).forEach(([k, v]) => {
      if (k !== 'name' && k !== 'params') {
        visit(v);
      }
    });
  };
  visit(node);
  return names;
};

/**
 * Map from path-key (e.g. `/0/params/1`) to the source position of that
 * node. Populated by `normalize` and consumed by the evaluator to attach
 * positions to diagnostics. Direct JS-object input has no entries — its
 * diagnostics surface without source positions.
 */
export type Spans = ReadonlyMap<string, SourcePos>;
/**
 * Resolve the source position at a path. When the exact node was not
 * spanned (an op handed a synthesized / non-source fragment to the
 * compiler, so the offending node has no own entry) climb to the
 * nearest spanned ancestor — the enclosing form's position is a far
 * more useful diagnostic anchor than `0:0`. Falls back to `NO_POS` only
 * when nothing on the path chain was spanned (e.g. direct JS-object
 * input with no spans sidecar).
 */
export const nodePos = (spans: Spans, path: string): SourcePos =>
  spans.get(path) ?? (path === '' ? NO_POS : nodePos(spans, parentPath(path)));

/**
 * Resolve the source position of an op call's head (its `name` token),
 * falling back to the call's overall position, then `NO_POS`. Used to
 * anchor `UnknownOpError`/`NotAnExpressionOpError`/`OpExecutionError` on
 * the op-name token rather than the wider call.
 */
export const opCallPos = (spans: Spans, path: string): SourcePos =>
  spans.get(nameKeyPath(path)) ?? nodePos(spans, path);

export type NormalizeResult = {
  readonly ast: readonly AstInput[];
  readonly spans: Spans;
};

/**
 * Convert a parsed AstNode[] script into the JS-object AST. The output keeps
 * one entry per top-level form. Source positions are extracted into the
 * spans sidecar so the JS objects are pure data.
 */
export const normalize = (forms: readonly AstNode[]): NormalizeResult => {
  const spans = new Map<string, SourcePos>();

  const walk = (node: AstNode, path: string): AstInput => {
    spans.set(path, node.pos);
    switch (node.kind) {
      case 'literal':
        return node.value;
      case 'symbol':
        return { $sym: node.name };
      case 'keyword':
        return { $kw: node.name };
      case 'vector':
        return node.elements.map((el, i) => walk(el, elemPath(path, i)));
      case 'map':
        return {
          $map: node.entries.map((entry, i) => ({
            k: entry.key.name,
            v: walk(entry.value, mapValuePath(path, entry.key.name, i)),
          })),
        };
      case 'list':
        return normalizeList(node.op, node.opPos, node.args, path);
      default: {
        // Exhaustiveness guard: a new `AstNode` kind fails to compile here
        // instead of being silently misclassified as a list.
        const unreachable: never = node;
        return unreachable;
      }
    }
  };

  const normalizeList = (op: string, opPos: SourcePos, args: readonly AstArg[], path: string): AstInput => {
    // Threading-macro desugar: `(-> seed s1 s2 …)` and `(->> seed s1 s2
    // …)` rewrite to nested op-calls at normalize time. This is the
    // single source of truth for threading semantics — typeCheck,
    // evaluator, and every walker downstream see the desugared AST. The
    // macros never reach the engine as opCalls (there is no runtime
    // `apply` for `->` / `->>`).
    if (op === '->' || op === '->>') {
      return desugarThread(op === '->' ? 'first' : 'last', opPos, args, path);
    }
    spans.set(nameKeyPath(path), opPos);
    // Every arg is positional. Schema-directed kwarg lifting happens in
    // `parseOpCall` against the op schema's declared kwarg keys.
    const positional = args.map((arg, i) => walk(arg.value, paramPath(path, i)));
    // Parser boundary: `op` is raw text. Whether it names a real op is the
    // runtime dispatcher's call (`resolveOpCall` → `UnknownOpError`); the
    // `Opcode` constraint governs static producers/guards, not parsed input.
    return { name: op as Opcode, params: positional };
  };

  // Rewrite a `(-> seed step1 step2 …)` (thread-first) or `(->> …)`
  // (thread-last) list into the equivalent nested op-call AST. Each step
  // wraps the running accumulator as either its first arg (thread-first)
  // or last arg (thread-last). The final desugared OpCall lives AT
  // `path`; intermediate calls nest under `params/0` (one level per step
  // remaining after that point). Spans are emitted at the desugared
  // paths so diagnostics point to the step that the agent wrote.
  const desugarThread = (
    position: 'first' | 'last',
    opPos: SourcePos,
    args: readonly AstArg[],
    path: string,
  ): AstInput => {
    if (args.length === 0) {
      // Parser enforces a non-empty list, but keep a precise diagnostic
      // for defence-in-depth.
      spans.set(path, opPos);
      throw new ShapeError('threading form requires a seed value', opPos);
    }
    const [seedArg, ...stepArgs] = args as readonly [AstArg, ...AstArg[]];
    // Seed lives nested under `path/params/0` N times, where N is the
    // number of steps. Walk it once at its final path.
    const seedFinalPath = stepArgs.reduce<string>((p) => paramPath(p, 0), path);
    const seedAst = walk(seedArg.value, seedFinalPath);
    return stepArgs.reduce<AstInput>((acc, stepArg, i) => {
      // Step i's desugared OpCall lives at depth `(stepArgs.length - 1
      // - i)` under `path`. The final step (i === stepArgs.length - 1)
      // lives at `path` itself.
      const remaining = stepArgs.length - 1 - i;
      const stepPath = Array.from({ length: remaining }).reduce<string>((p) => paramPath(p, 0), path);
      return wrapThreadStep(stepArg.value, acc, position, stepPath);
    }, seedAst);
  };

  // Build the desugared OpCall for one threading step, wrapping `acc`
  // (the running accumulator) at the position dictated by the macro
  // form. Supports the three step kinds:
  //   - `(f a b)`  — opCall, walked recursively with `acc` injected.
  //   - `f`        — bare symbol, lifted to `(f acc)`.
  //   - `:k`       — bare keyword, lifted to `(col acc :k)`.
  // Spans for the synthetic OpCall are set at `stepPath` (the desugared
  // path); the step's own args are walked at their final paths under
  // `stepPath/params/i` so source positions follow them to the
  // desugared shape.
  const wrapThreadStep = (stepNode: AstNode, acc: AstInput, position: 'first' | 'last', stepPath: string): AstInput => {
    spans.set(stepPath, stepNode.pos);
    if (stepNode.kind === 'list') {
      // A source op (`query` / `datapoints`) opens its own pipeline; it can
      // never be a thread step — threading would feed `acc` into its
      // view-name slot, surfacing later as an opaque `expected one of:
      // string`. Fail here with the actual mistake instead.
      if (SOURCE_OP_NAMES.has(stepNode.op)) {
        throw new SourceOpInPipelineError(stepNode.op, stepNode.opPos);
      }
      spans.set(nameKeyPath(stepPath), stepNode.opPos);
      // Walk the step's existing args at their final paths in the
      // desugared OpCall. For thread-first, `acc` occupies params[0]
      // and the existing args shift to params[1..N]. For thread-last,
      // `acc` occupies params[N] and existing args stay at params[0..N-1].
      const existing = stepNode.args.map((arg, i) => {
        const finalIdx = position === 'first' ? i + 1 : i;
        return walk(arg.value, paramPath(stepPath, finalIdx));
      });
      const params = position === 'first' ? [acc, ...existing] : [...existing, acc];
      return { name: stepNode.op as Opcode, params };
    }
    if (stepNode.kind === 'symbol') {
      spans.set(nameKeyPath(stepPath), stepNode.pos);
      return { name: stepNode.name as Opcode, params: [acc] };
    }
    if (stepNode.kind === 'keyword') {
      // Synthesise `(col acc :k)` — same shape the keyword-head
      // desugar (`(:k target …)` → `(col target :k …)`) produces.
      spans.set(nameKeyPath(stepPath), stepNode.pos);
      return {
        name: 'col',
        params: [acc, { $kw: stepNode.name }],
      };
    }
    throw new ShapeError(`threading step must be an op call, symbol, or keyword; got ${stepNode.kind}`, stepNode.pos);
  };

  const ast = forms.map((f, i) => walk(f, elemPath('', i)));
  return { ast, spans };
};
